/** Modelo de dominio de NovaBerry. */

/** "despacho" es una identidad compartida, no una persona: quien reparte
 *  entra como "Despachos" para ver la ruta del día, sin pasar por el
 *  resto de la app. */
export type Rol = 'admin' | 'vendedor' | 'despacho'

export interface Usuario {
  id: string
  nombre: string
  /** Solo informativo: NovaBerry no tiene cuentas por persona, así que
   *  el correo ya no identifica a nadie ni sirve para entrar. */
  email?: string
  telefono?: string
  rol: Rol
  activo: boolean
  /** Porcentaje de comisión sobre la GANANCIA de cada venta —lo vendido
   *  menos el costo—, no sobre el total (30 = 30%). Si un producto costó
   *  $1.000 y se vendió en $2.000, la comisión sale de esos $1.000.
   *  Cada vendedor tiene la suya; se define en Vendedores. */
  comision: number
  creado: string
  ultimaConexion?: string
  /** A diferencia del resto del material de marketing mensual (que es
   *  el mismo para todos), la tarjeta de presentación es propia de
   *  cada vendedor —se sube desde su perfil, no desde Ajustes. */
  tarjetaPresentacion?: string
  /** Acceso a NovaBerry POS, la app de los locales. Esta app no pide
   *  PIN —el vendedor en terreno solo toca su nombre—, pero sí lo
   *  define desde la ficha de la sucursal, porque el local sí lo pide.
   *  El PIN nunca se guarda tal cual: solo su hash con sal. */
  pinHash?: string
  pinSalt?: string
  /** A qué locales puede entrar en POS. Vacío o sin definir = a todos
   *  (es lo que corresponde a un administrador). */
  sucursales?: string[]
  /** Clave para entrar como administrador a NovaBerry (esta app, no
   *  POS) desde la pantalla de "elegir persona". Campos aparte de
   *  `pinHash`/`pinSalt` a propósito: esos son del PIN de POS en un
   *  local, esto es otra cosa —ver `src/lib/claveAdmin.ts`—. Sin
   *  definir, ese administrador entra igual que cualquier vendedor,
   *  solo tocando su nombre. */
  claveHash?: string
  claveSal?: string
}

export type Categoria = 'frutas' | 'miel' | 'avena' | 'otros'

export const CATEGORIAS: { valor: Categoria; etiqueta: string }[] = [
  { valor: 'frutas', etiqueta: 'Frutas' },
  { valor: 'miel', etiqueta: 'Miel' },
  { valor: 'avena', etiqueta: 'Avena' },
  { valor: 'otros', etiqueta: 'Otros' },
]

export type Unidad = 'kg' | 'g' | 'unidad' | 'caja' | 'bandeja' | 'litro'

export interface Producto {
  id: string
  codigo: string
  nombre: string
  categoria: Categoria
  /** Peso o contenido por unidad de venta, en la unidad indicada. */
  peso: number
  unidad: Unidad
  /** Solo el administrador la ve y la edita — nunca se muestra a un
   *  vendedor, ni en pantalla ni en exportaciones. */
  precioCosto: number
  precioSugerido: number
  /** Piso de venta a clientes mayoristas (pastelerías, etc.). A
   *  diferencia de precioCosto, esto SÍ lo ve el vendedor: es la
   *  referencia para no vender más barato de la cuenta. */
  precioMinimoMayorista?: number
  /** Piso de venta cuando la cantidad es chica (menos de 3 kilos). */
  precioMinimoDetalle?: number
  /** Piso de venta mayorista cuando el pedido de este producto llega a
   *  10 kilos o más —más barato que `precioMinimoMayorista` porque el
   *  volumen lo justifica. Solo aplica a clientes empresa. */
  precioMinimoMayoristaVolumen?: number
  stock: number
  /** Unidades ya comprometidas en pedidos sin despachar. */
  reservado: number
  /** Umbral propio; si es null se usa el umbral global de ajustes. */
  stockMinimo: number | null
  foto?: string
  codigoBarras?: string
  activo: boolean
  /** Posición fija en el catálogo (más chico = más arriba). Los que no
   *  tienen valor van al final, ordenados por nombre. */
  ordenCatalogo?: number
  creado: string
}

/** Un lote es una entrada de stock con vencimiento propio. Se vende
 *  primero el más antiguo (FEFO: first expired, first out). */
export interface Lote {
  id: string
  productoId: string
  codigo: string
  cantidad: number
  cantidadInicial: number
  vencimiento?: string
  costo: number
  proveedor?: string
  /** Quién recibió físicamente la mercadería —no siempre es quien
   *  carga los datos en el sistema. */
  recibidoPor?: string
  ingresado: string
}

export type TipoMovimiento = 'ingreso' | 'venta' | 'ajuste' | 'devolucion' | 'merma'

export interface Movimiento {
  id: string
  productoId: string
  tipo: TipoMovimiento
  /** Positiva suma al stock, negativa resta. */
  cantidad: number
  stockResultante: number
  loteId?: string
  proveedor?: string
  costo?: number
  /** Quién recibió físicamente la mercadería (solo ingresos). */
  recibidoPor?: string
  referenciaId?: string
  usuarioId: string
  usuarioNombre: string
  observaciones?: string
  fecha: string
}

export type TipoCliente = 'empresa' | 'particular'

export type RubroEmpresa =
  | 'pasteleria'
  | 'restaurante'
  | 'cafeteria'
  | 'hotel'
  | 'distribuidor'
  | 'otro'

export const RUBROS: { valor: RubroEmpresa; etiqueta: string }[] = [
  { valor: 'pasteleria', etiqueta: 'Pastelería' },
  { valor: 'restaurante', etiqueta: 'Restaurante' },
  { valor: 'cafeteria', etiqueta: 'Cafetería' },
  { valor: 'hotel', etiqueta: 'Hotel' },
  { valor: 'distribuidor', etiqueta: 'Distribuidor' },
  { valor: 'otro', etiqueta: 'Otro' },
]

export interface Cliente {
  id: string
  tipo: TipoCliente
  /** Razón social si es empresa, nombre de pila si es particular. */
  nombre: string
  apellido?: string
  rubro?: RubroEmpresa
  rut?: string
  direccion?: string
  /** Depto, oficina, local o casa dentro de un condominio/strip center
   *  —lo que la dirección misma no dice y el despachador necesita para
   *  no quedarse tocando el timbre equivocado. */
  local?: string
  comuna?: string
  telefono?: string
  email?: string
  contacto?: string
  observaciones?: string
  lat?: number
  lng?: number
  /** Cada cuántos días este cliente vuelve a pedir. Se usa para avisarle
   *  al vendedor que ya toca volver a ofrecerle, sin esperar a que el
   *  cliente llame primero. */
  frecuenciaPedidoDias?: number
  creado: string
}

export type MetodoPago = 'efectivo' | 'transferencia' | 'tarjeta' | 'credito'

export const METODOS_PAGO: { valor: MetodoPago; etiqueta: string; icono: string }[] = [
  { valor: 'efectivo', etiqueta: 'Efectivo', icono: 'Banknote' },
  { valor: 'transferencia', etiqueta: 'Transferencia', icono: 'ArrowLeftRight' },
  { valor: 'tarjeta', etiqueta: 'Tarjeta', icono: 'CreditCard' },
  { valor: 'credito', etiqueta: 'Crédito', icono: 'CalendarClock' },
]

export type EstadoPedido = 'pendiente' | 'preparando' | 'despachada' | 'entregada' | 'anulada'

export const ESTADOS_PEDIDO: { valor: EstadoPedido; etiqueta: string }[] = [
  { valor: 'pendiente', etiqueta: 'Pendiente' },
  { valor: 'preparando', etiqueta: 'Preparando' },
  { valor: 'despachada', etiqueta: 'Despachado' },
  { valor: 'entregada', etiqueta: 'Entregado' },
  { valor: 'anulada', etiqueta: 'Anulado' },
]

/** Antes de salir a repartir, el despachador llama o escribe a cada
 *  cliente del día. Solo salen a la ruta los confirmados; los otros dos
 *  casos se corren al siguiente día de reparto. */
export type ConfirmacionCliente = 'confirmado' | 'no_contesto' | 'no_confirmado'

export const CONFIRMACIONES: {
  valor: ConfirmacionCliente
  etiqueta: string
  detalle: string
}[] = [
  { valor: 'confirmado', etiqueta: 'Confirmado', detalle: 'El cliente espera el pedido hoy' },
  { valor: 'no_contesto', etiqueta: 'No contestó', detalle: 'Se corre al próximo día de reparto' },
  { valor: 'no_confirmado', etiqueta: 'No confirmó', detalle: 'Se corre al próximo día de reparto' },
]

export type DiaDespacho = 'martes' | 'jueves' | 'especial'

export const DIAS_DESPACHO: { valor: DiaDespacho; etiqueta: string }[] = [
  { valor: 'especial', etiqueta: 'Primer despacho' },
  { valor: 'martes', etiqueta: 'Martes' },
  { valor: 'jueves', etiqueta: 'Jueves' },
]

/** Cómo terminó de verdad una entrega. Antes lo único que se podía
 *  registrar era "entregado": si el cliente no estaba, rechazaba el
 *  pedido o recibía menos de lo que iba en el camión, no había dónde
 *  anotarlo y el pedido quedaba dando vueltas sin explicación. */
export type ResultadoEntrega = 'entregado' | 'parcial' | 'ausente' | 'rechazado'

export const RESULTADOS_ENTREGA: {
  valor: ResultadoEntrega
  etiqueta: string
  detalle: string
}[] = [
  { valor: 'entregado', etiqueta: 'Entregado', detalle: 'Recibió todo lo que iba en el pedido' },
  { valor: 'parcial', etiqueta: 'Entrega parcial', detalle: 'Recibió menos de lo que iba: se anota cuánto' },
  { valor: 'ausente', etiqueta: 'No estaba', detalle: 'No había quién recibiera: se corre al próximo reparto' },
  { valor: 'rechazado', etiqueta: 'Rechazó el pedido', detalle: 'No lo quiso recibir: vuelve el producto' },
]

/** De qué lote salió cada parte de una línea de venta. Una misma línea
 *  puede repartirse entre varios lotes cuando el primero no alcanza
 *  —pasa seguido con fruta que llega en partidas chicas—. */
export interface LoteVendido {
  loteId: string
  /** Código legible del lote, copiado al vender: si el lote se borra o
   *  cambia, la venta conserva de cuál salió. */
  codigo?: string
  cantidad: number
  /** Costo unitario de ESE lote, que puede diferir del de otros. */
  costo: number
}

export interface LineaVenta {
  productoId: string
  /** Copia del nombre y código al momento de vender: el producto puede
   *  cambiar de nombre después y la venta no debe cambiar con él. */
  nombre: string
  codigo: string
  cantidad: number
  precioUnitario: number
  costoUnitario: number
  subtotal: number
  /** El primer lote consumido. Se mantiene por las ventas antiguas que
   *  solo tienen este dato; para trazabilidad usar `lotes`. */
  loteId?: string
  /** TODOS los lotes de los que salió esta línea, con cuánto salió de
   *  cada uno. Es lo que permite responder "a qué clientes les llegó el
   *  lote X" ante un retiro sanitario —con solo `loteId` se perdía el
   *  rastro de la segunda mitad de la venta. */
  lotes?: LoteVendido[]
  /** Cuánto recibió de verdad el cliente, si fue distinto a lo enviado
   *  (entrega parcial). Si va vacío, se entregó todo. */
  cantidadEntregada?: number
}

export interface Venta {
  id: string
  folio: string
  clienteId: string
  clienteNombre: string
  clienteTipo: TipoCliente
  usuarioId: string
  usuarioNombre: string
  lineas: LineaVenta[]
  total: number
  costoTotal: number
  utilidad: number
  /** Comisión del vendedor sobre esta venta, calculada con la tasa que
   *  tenía al momento de venderla. Si la tasa cambia después, esta
   *  venta no cambia con ella — igual que el costo de cada línea. */
  comision: number
  metodoPago: MetodoPago
  estado: EstadoPedido
  /** Si ya entró la plata. Ojo: una venta en efectivo o transferencia
   *  que se entrega días después NO nace pagada —la plata llega recién
   *  en la entrega—. Solo nace pagada la que se entrega en el momento. */
  pagado: boolean
  vencimientoPago?: string
  /** Cuándo y quién registró el pago. Sirve para cuadrar el efectivo
   *  que trae de vuelta el despachador al final del día. */
  fechaPago?: string
  cobradoPorId?: string
  cobradoPorNombre?: string
  /** Cómo terminó la entrega de verdad. Si va vacío en una venta ya
   *  entregada, es de antes de que esto existiera: se asume entregada
   *  completa. */
  resultadoEntrega?: ResultadoEntrega
  /** Lo que anotó el despachador cuando la entrega no salió redonda
   *  (no estaba, rechazó, recibió menos). */
  notaEntrega?: string
  /** Cuándo se marcó como entregada. Sirve para avisar si un cliente
   *  lleva mucho tiempo sin recibir un pedido nuevo. */
  fechaEntrega?: string
  /** Martes, jueves o un día especial —lo elige el vendedor al vender. */
  diaDespacho?: DiaDespacho
  /** La fecha concreta que le corresponde (el próximo martes/jueves,
   *  o la fecha especial elegida). Es lo que usa Despachos para armar
   *  la ruta del día. */
  fechaDespacho?: string
  /** Si no se llena, Despachos usa la dirección del cliente. */
  direccionDespacho?: string
  /** Lo que se le pagó al despachador por esta entrega, congelado en el
   *  momento de entregarla. Solo lo llevan los despachos fuera de la
   *  ruta programada —los de martes y jueves no se pagan aparte—.
   *
   *  Se guarda en vez de recalcularse después por la misma razón que la
   *  comisión: si mañana sube el tarifario, los viajes de la semana
   *  pasada no pueden cambiar de precio hacia atrás. Antes se calculaba
   *  al vuelo y el "cuánto llevo ganado" del despachador se movía solo
   *  cuando alguien tocaba Ajustes. */
  tarifaDespacho?: number
  /** Los kilómetros con que se calculó esa tarifa. Se guardan para
   *  poder explicar el monto sin tener que volver a geocodificar. */
  kmDespacho?: number
  /** El vendedor ya dejó la entrega acordada con el cliente al momento
   *  de venderle, así que el despachador no tiene que volver a
   *  confirmarla: el pedido entra directo a la hoja de ruta de la fecha
   *  elegida, con su tarifa. Solo se ofrece para clientes que ya
   *  compraron antes —a uno nuevo hay que llamarlo igual. */
  entregaSinAviso?: boolean
  /** El despachador tiene que confirmar que recibió y revisó cada
   *  pedido antes de ver la hoja de ruta del día —así no sale a
   *  repartir algo que en realidad no está listo o tiene un problema. */
  revisadoDespacho?: boolean
  /** Si el despachador encuentra algo mal (falta stock, la dirección no
   *  cuadra, etc.) lo anota acá en vez de confirmar a ciegas. */
  erroDespacho?: string
  /** Resultado del contacto con el cliente antes de salir a repartir.
   *  Solo los confirmados entran a la ruta del día. Admite null a
   *  propósito: al guardar en Supabase, un `undefined` se pierde en el
   *  JSON y la columna se quedaría con el valor anterior. */
  confirmacionCliente?: ConfirmacionCliente | null
  /** Cuántas veces se corrió de día por no lograr confirmar. Sirve para
   *  que un cliente inubicable no quede rebotando en silencio para
   *  siempre. */
  vecesPospuesto?: number
  observaciones?: string
  fecha: string
  /** Si esta venta ya tiene su factura emitida. Solo importa para
   *  `clienteTipo === 'empresa'` —a un particular no se le factura
   *  desde acá—. Mientras esté en false, el administrador la ve como
   *  pendiente en Inicio. */
  facturaEmitida?: boolean
  /** El número de la factura tal como quedó en el sistema de
   *  facturación de verdad (SII, Bsale, etc.) —esta app no emite
   *  facturas, solo lleva la cuenta de cuáles ya se hicieron. */
  facturaNumero?: string
  facturaFecha?: string
  facturaPorNombre?: string
}

export interface Devolucion {
  id: string
  ventaId: string
  ventaFolio: string
  productoId: string
  productoNombre: string
  cantidad: number
  monto: number
  motivo: string
  /** Si el producto vuelve vendible se repone al stock. */
  reponeStock: boolean
  usuarioId: string
  usuarioNombre: string
  fecha: string
}

export type AccionBitacora =
  | 'inicio_sesion'
  | 'cierre_sesion'
  | 'crear'
  | 'editar'
  | 'eliminar'
  | 'venta'
  | 'ingreso_stock'
  | 'devolucion'
  | 'exportar'
  | 'ajuste'

export interface Bitacora {
  id: string
  usuarioId: string
  usuarioNombre: string
  accion: AccionBitacora
  entidad: string
  entidadId?: string
  detalle: string
  fecha: string
}

export interface Ajustes {
  id: string
  /** Umbral global de "poco stock" cuando el producto no define el suyo. */
  stockMinimoGlobal: number
  /** Días de aviso antes del vencimiento de un lote. */
  diasAvisoVencimiento: number
  /** Días de plazo por defecto para ventas a crédito. */
  diasCredito: number
  nombreEmpresa: string
  rutEmpresa: string
  direccionEmpresa: string
  telefonoEmpresa: string
  emailEmpresa: string
  moneda: string
  /** Los precios de venta de la app son netos —sin IVA—; este
   *  porcentaje es el que se suma para mostrar el total con IVA en
   *  Gastos y en los reportes financieros. Chile: 19%. */
  porcentajeIva: number
  /** Categorías de gasto que el administrador arma a su gusto (Gastos
   *  fijos, Publicidad, etc.). Empiezan con algunas típicas. */
  categoriasGasto: string[]
  /** Punto de partida de la ruta de despacho. */
  bodegaDireccion: string
  bodegaLat: number
  bodegaLng: number
  /** Quién reparte hoy —se muestra arriba en Despachos, con botón de
   *  llamar/WhatsApp directo. */
  despachadorNombre: string
  despachadorTelefono: string
  /** Tarifario "tipo Uber" para los despachos de "Primer despacho": los
   *  que no caen en la ruta programada del martes o jueves, así que el
   *  despachador los hace como una salida propia y le corresponde un
   *  pago aparte. Tarifa = base + (km en línea recta desde la bodega ×
   *  tarifa por km), igual que el cálculo de una tarifa de app de
   *  transporte. */
  tarifaDespachoBase: number
  tarifaDespachoPorKm: number
  /** Lo que se le paga al despachador por llevar un envío de la bodega
   *  central a una sucursal. Es un monto fijo y no por kilómetro: las
   *  sucursales son siempre las mismas y el viaje es el mismo todas las
   *  veces, así que cobrar por distancia solo agregaría un cálculo que
   *  siempre da lo mismo. */
  tarifaTraspasoSucursal: number
  /** Material de apoyo del mes: el administrador lo sube desde el
   *  perfil y cada vendedor lo descarga desde el suyo. Se guardan como
   *  URL de datos (base64), igual que la foto de un producto —no hay
   *  bucket de archivos configurado. */
  marketingMapaCobertura?: string
  marketingPreciosDetalle?: string
  marketingPreciosMayorista?: string
  marketingTarjetaPresentacion?: string
  marketingOfertasSemana?: string
}

export type TipoGasto = 'fijo' | 'variable' | 'publicidad' | 'compra' | 'otro'

export const TIPOS_GASTO: { valor: TipoGasto; etiqueta: string }[] = [
  { valor: 'fijo', etiqueta: 'Gasto fijo' },
  { valor: 'variable', etiqueta: 'Gasto variable' },
  { valor: 'publicidad', etiqueta: 'Publicidad' },
  { valor: 'compra', etiqueta: 'Compra' },
  { valor: 'otro', etiqueta: 'Otro' },
]

/** Un gasto de la empresa: arriendo, luz, publicidad, una compra
 *  puntual, lo que sea. Existe para que el administrador no tenga que
 *  llevar esto aparte en una planilla. */
export interface Gasto {
  id: string
  categoria: string
  tipo: TipoGasto
  descripcion: string
  /** Lo que realmente se pagó (el monto total, con IVA si corresponde). */
  monto: number
  /** Si el gasto tiene factura con IVA, se puede descontar como
   *  crédito fiscal; si es boleta o no tiene IVA, no. */
  afectoIva: boolean
  proveedor?: string
  observaciones?: string
  usuarioId: string
  usuarioNombre: string
  fecha: string
}

/** Marca de entrada/salida de alguien en una sucursal (de las de
 *  `pos_sucursales`, el registro único que comparten las dos apps).
 *  Vive solo acá: no tiene relación con los turnos de caja de POS, que
 *  son otra cosa —esto es quién estuvo, aquello es cuánta plata hubo. */
export interface Asistencia {
  id: string
  sucursalId: string
  usuarioId: string
  usuarioNombre: string
  entrada: string
  /** null mientras la persona sigue trabajando. */
  salida?: string | null
}

/** Estado de stock derivado, con el color del espectro que le toca. */
export type EstadoStock = 'disponible' | 'poco' | 'agotado'

export interface Notificacion {
  id: string
  tipo: 'stock_bajo' | 'agotado' | 'vencimiento' | 'cobranza' | 'seguimiento'
  titulo: string
  detalle: string
  enlace?: string
  fecha: string
  leida: boolean
}
